import { Resource } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';
import { findControllerFiles, findSmartStackStructure } from '../lib/detector.js';
import { readText } from '../utils/fs.js';
import path from 'path';

export const apiEndpointsResourceTemplate: Resource = {
  uri: 'smartstack://api/',
  name: 'SmartStack API Endpoints',
  description: 'API endpoint documentation. Use smartstack://api/{endpoint} to filter.',
  mimeType: 'text/markdown',
};

interface ParsedEndpoint {
  method: string;
  path: string;
  controller: string;
  action: string;
  parameters: string[];
  returnType: string;
  authorize: boolean;
  summary?: string;
}

export async function getApiEndpointsResource(
  config: Config,
  endpointFilter?: string
): Promise<string> {
  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  if (!structure.api) {
    return '# API Endpoints\n\nNo API project found.';
  }

  const controllerFiles = await findControllerFiles(structure.api);
  const allEndpoints: ParsedEndpoint[] = [];

  for (const file of controllerFiles) {
    const endpoints = await parseController(file, structure.root);
    allEndpoints.push(...endpoints);
  }

  // Filter if specified
  const endpoints = endpointFilter
    ? allEndpoints.filter(e =>
      e.path.toLowerCase().includes(endpointFilter.toLowerCase()) ||
      e.controller.toLowerCase().includes(endpointFilter.toLowerCase())
    )
    : allEndpoints;

  return formatEndpoints(endpoints, endpointFilter);
}

async function parseController(filePath: string, _rootPath: string): Promise<ParsedEndpoint[]> {
  const content = await readText(filePath);
  const fileName = path.basename(filePath, '.cs');
  const controllerName = fileName.replace('Controller', '');
  const endpoints: ParsedEndpoint[] = [];

  // Parse class-level route
  const routeMatch = content.match(/\[Route\s*\(\s*"([^"]+)"\s*\)\]/);
  const baseRoute = routeMatch
    ? routeMatch[1].replace('[controller]', controllerName.toLowerCase())
    : `/api/${controllerName.toLowerCase()}`;

  // Check for class-level Authorize
  const classAuthorize = /\[Authorize\s*(?:\([^)]*\))?\s*\]\s*(?:\[.*\]\s*)*public\s+class/.test(content);

  // Simple approach: find all HTTP method attributes
  const httpMethods = ['HttpGet', 'HttpPost', 'HttpPut', 'HttpPatch', 'HttpDelete'];
  let match: RegExpExecArray | null;

  for (const httpMethod of httpMethods) {
    const regex = new RegExp(
      `\\[${httpMethod}(?:\\s*\\(\\s*"([^"]*)"\\s*\\))?\\]\\s*(?:\\[.*?\\]\\s*)*public\\s+(?:async\\s+)?(?:Task<)?(?:ActionResult<)?(\\w+)(?:[<>\\[\\],\\s\\w]*)?\\s+(\\w+)\\s*\\(([^)]*)\\)`,
      'g'
    );

    while ((match = regex.exec(content)) !== null) {
      const routeSuffix = match[1] || '';
      const returnType = match[2];
      const actionName = match[3];
      const params = match[4];

      // Build full path
      let fullPath = baseRoute;
      if (routeSuffix) {
        fullPath = `${baseRoute}/${routeSuffix}`;
      }

      // Parse parameters
      const parameters = parseParameters(params);

      // Check for method-level authorize
      const methodAuthorize = content.substring(Math.max(0, match.index - 200), match.index)
        .includes('[Authorize');

      endpoints.push({
        method: httpMethod.replace('Http', '').toUpperCase(),
        path: fullPath.replace(/\/+/g, '/'),
        controller: controllerName,
        action: actionName,
        parameters,
        returnType,
        authorize: classAuthorize || methodAuthorize,
      });
    }
  }

  return endpoints;
}

function parseParameters(paramsString: string): string[] {
  if (!paramsString.trim()) return [];

  const params: string[] = [];
  const parts = paramsString.split(',');

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Extract parameter name
    const match = trimmed.match(/(\w+)\s*(?:=.*)?$/);
    if (match) {
      // Include source attribute if present
      if (trimmed.includes('[FromBody]')) {
        params.push(`body: ${match[1]}`);
      } else if (trimmed.includes('[FromQuery]')) {
        params.push(`query: ${match[1]}`);
      } else if (trimmed.includes('[FromRoute]')) {
        params.push(`route: ${match[1]}`);
      } else {
        params.push(match[1]);
      }
    }
  }

  return params;
}

function formatEndpoints(endpoints: ParsedEndpoint[], filter?: string): string {
  const lines: string[] = [];

  lines.push('# SmartStack API Endpoints');
  lines.push('');

  if (filter) {
    lines.push(`> Filtered by: \`${filter}\``);
    lines.push('');
  }

  if (endpoints.length === 0) {
    lines.push('No endpoints found matching the criteria.');
    return lines.join('\n');
  }

  // Group by controller
  const byController = new Map<string, ParsedEndpoint[]>();
  for (const endpoint of endpoints) {
    const existing = byController.get(endpoint.controller) || [];
    existing.push(endpoint);
    byController.set(endpoint.controller, existing);
  }

  // Summary table
  lines.push('## Summary');
  lines.push('');
  lines.push(`**Total Endpoints**: ${endpoints.length}`);
  lines.push(`**Controllers**: ${byController.size}`);
  lines.push('');

  // Method distribution
  const methodCounts = new Map<string, number>();
  for (const endpoint of endpoints) {
    methodCounts.set(endpoint.method, (methodCounts.get(endpoint.method) || 0) + 1);
  }

  lines.push('| Method | Count |');
  lines.push('|--------|-------|');
  for (const [method, count] of methodCounts) {
    lines.push(`| ${method} | ${count} |`);
  }
  lines.push('');

  // Detailed by controller
  for (const [controller, controllerEndpoints] of byController) {
    lines.push(`## ${controller}Controller`);
    lines.push('');

    // Sort by path then method
    controllerEndpoints.sort((a, b) => {
      const pathCompare = a.path.localeCompare(b.path);
      if (pathCompare !== 0) return pathCompare;
      return a.method.localeCompare(b.method);
    });

    for (const endpoint of controllerEndpoints) {
      const authBadge = endpoint.authorize ? ' 🔒' : '';
      const methodColor = getMethodEmoji(endpoint.method);

      lines.push(`### ${methodColor} \`${endpoint.method}\` ${endpoint.path}${authBadge}`);
      lines.push('');
      lines.push(`**Action**: \`${endpoint.action}\``);
      lines.push(`**Returns**: \`${endpoint.returnType}\``);

      if (endpoint.parameters.length > 0) {
        lines.push('');
        lines.push('**Parameters**:');
        for (const param of endpoint.parameters) {
          lines.push(`- \`${param}\``);
        }
      }

      lines.push('');
    }
  }

  // Quick reference table
  lines.push('## Quick Reference');
  lines.push('');
  lines.push('| Method | Path | Action | Auth |');
  lines.push('|--------|------|--------|------|');
  for (const endpoint of endpoints) {
    lines.push(
      `| ${endpoint.method} | \`${endpoint.path}\` | ${endpoint.action} | ${endpoint.authorize ? '🔒' : '✓'} |`
    );
  }
  lines.push('');

  return lines.join('\n');
}

function getMethodEmoji(method: string): string {
  switch (method) {
    case 'GET': return '🔵';
    case 'POST': return '🟢';
    case 'PUT': return '🟡';
    case 'PATCH': return '🟠';
    case 'DELETE': return '🔴';
    default: return '⚪';
  }
}
